import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  Download,
  Eye,
  FileUp,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/ConfirmDialog";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  archiveCompanyDocument,
  deleteCompanyDocument,
  fetchCompanyDocuments,
  replaceCompanyDocumentFile,
  restoreCompanyDocument,
  updateCompanyDocument,
  uploadCompanyDocument,
} from "@/services/companyDocumentService";
import CompanyDocumentFormDialog from "@/components/company-documents/CompanyDocumentFormDialog";
import CompanyDocumentAcknowledgementsDialog from "@/components/company-documents/CompanyDocumentAcknowledgementsDialog";
import {
  CompanyDocumentActiveCategory,
  CompanyDocumentCategoryFilter,
  CompanyDocumentCategoryTabs,
  CompanyDocumentPreviewDialog,
  CompanyDocumentsError,
  CompanyDocumentsGrid,
  CompanyDocumentsSkeleton,
  CompanyDocumentsToolbar,
  SegmentedControl,
} from "@/components/company-documents/CompanyDocumentsList";
import {
  canPreview,
  downloadCompanyDocument,
  includesText,
  openCompanyDocument,
} from "@/components/company-documents/companyDocumentUtils";

const FEATURE = "company_documents";

const reportError = (error, fallback) => {
  if (!isAuthExpiryError(error)) toast.error(getErrorMessage(error, fallback));
};

const CompanyDocuments = () => {
  const [activeDocs, setActiveDocs] = useState([]);
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [status, setStatus] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");

  const [formState, setFormState] = useState({ open: false, mode: "create", document: null });
  const [confirmState, setConfirmState] = useState({ open: false, action: null, document: null });
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [acknowledgementsDoc, setAcknowledgementsDoc] = useState(null);

  const { can } = usePermission();
  const { canCreate, canEdit, canDelete } = createCanHelpers(can);
  const canCreateDoc = canCreate(FEATURE);
  const canEditDoc = canEdit(FEATURE);
  const canDeleteDoc = canDelete(FEATURE);

  const loadDocuments = useCallback(async () => {
    setLoadError(null);
    try {
      const [active, archived] = await Promise.all([
        fetchCompanyDocuments("active"),
        fetchCompanyDocuments("archived"),
      ]);
      setActiveDocs(active);
      setArchivedDocs(archived);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        setLoadError(getErrorMessage(error, "Failed to load company documents"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Search narrows first so the category tab counts match what is shown.
  const searchedDocs = useMemo(() => {
    const source = status === "archived" ? archivedDocs : activeDocs;
    return source.filter((doc) => includesText(doc, searchTerm));
  }, [status, activeDocs, archivedDocs, searchTerm]);

  const visibleDocs = useMemo(
    () => (category === "all" ? searchedDocs : searchedDocs.filter((d) => d.category === category)),
    [searchedDocs, category],
  );

  const handleStatusChange = (next) => {
    setStatus(next);
    setCategory("all");
  };

  /* ---------------- actions ---------------- */

  const openForm = (mode, document = null) => {
    const allowed = mode === "create" ? canCreate(FEATURE, true) : canEdit(FEATURE, true);
    if (!allowed) return;
    setFormState({ open: true, mode, document });
  };

  const handleFormSubmit = async ({
    title,
    category: cat,
    description,
    file,
    audience,
    audienceChanged,
    acknowledgementRequired,
  }) => {
    const { mode, document } = formState;
    try {
      if (mode === "create") {
        const formData = new FormData();
        formData.append("title", title);
        formData.append("category", cat);
        formData.append("description", description);
        formData.append("audience", JSON.stringify(audience));
        formData.append("acknowledgement_required", String(acknowledgementRequired));
        formData.append("file", file);
        await uploadCompanyDocument(formData);
        toast.success("Document uploaded successfully");
      } else if (mode === "edit") {
        // Only send the audience when it changed, so saving other details never
        // re-validates an audience the plan no longer allows creating.
        await updateCompanyDocument(document._id, {
          title,
          category: cat,
          description,
          acknowledgement_required: acknowledgementRequired,
          ...(audienceChanged ? { audience } : {}),
        });
        toast.success("Document details updated");
      } else {
        const formData = new FormData();
        formData.append("file", file);
        await replaceCompanyDocumentFile(document._id, formData);
        toast.success("Document file replaced");
      }
      await loadDocuments();
    } catch (error) {
      reportError(error, "Failed to save document");
      throw error;
    }
  };

  const askConfirm = (action, document) => {
    if (!canDelete(FEATURE, true)) return;
    setConfirmState({ open: true, action, document });
  };

  const handleConfirm = async () => {
    const { action, document } = confirmState;
    if (!document) return;
    setBusyId(document._id);
    try {
      if (action === "archive") {
        await archiveCompanyDocument(document._id);
        toast.success(`"${document.title}" archived`);
      } else if (action === "delete") {
        await deleteCompanyDocument(document._id);
        toast.success(`"${document.title}" deleted permanently`);
      }
      await loadDocuments();
    } catch (error) {
      reportError(error, action === "delete" ? "Failed to delete document" : "Failed to archive document");
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (document) => {
    if (!canDelete(FEATURE, true)) return;
    setBusyId(document._id);
    try {
      await restoreCompanyDocument(document._id);
      toast.success(`"${document.title}" restored`);
      await loadDocuments();
    } catch (error) {
      reportError(error, "Failed to restore document");
    } finally {
      setBusyId(null);
    }
  };

  const handleView = async (document) => {
    try {
      const url = await openCompanyDocument(document);
      if (url) setPreview({ doc: document, url });
    } catch (error) {
      reportError(error, "Failed to open document");
    }
  };

  const handleDownload = async (document) => {
    setBusyId(document._id);
    try {
      const fileName = await downloadCompanyDocument(document);
      toast.success(`Downloading ${fileName}`);
    } catch (error) {
      reportError(error, "Download failed");
    } finally {
      setBusyId(null);
    }
  };

  // Clicking a title views what the browser can show and downloads the rest.
  const handleOpen = (doc) => (canPreview(doc) ? handleView(doc) : handleDownload(doc));

  /* ---------------- render ---------------- */

  const disabledClass = "opacity-70 cursor-not-allowed";

  const renderActions = (doc) => {
    const busy = busyId === doc._id;
    return (
      <>
        {canPreview(doc) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleView(doc)}
            aria-label={`View ${doc.title}`}
            title="View"
            className="h-9 w-9 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:h-8 sm:w-8"
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleDownload(doc)}
          disabled={busy}
          aria-label={`Download ${doc.title}`}
          title="Download"
          className="h-9 w-9 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:h-8 sm:w-8"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </Button>
      </>
    );
  };

  const renderStatus = (doc) => {
    const summary = doc.acknowledgement_summary;
    if (!doc.acknowledgement_required || !summary) return null;
    const percent = summary.total ? Math.round((summary.acknowledged / summary.total) * 100) : 0;
    return (
      <button
        type="button"
        onClick={() => setAcknowledgementsDoc(doc)}
        aria-label={`See acknowledgements for ${doc.title}`}
        className="mt-3 block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">Acknowledged</span>
          <span className="font-medium text-primary hover:underline">
            {summary.acknowledged} of {summary.total}
          </span>
        </span>
        <Progress value={percent} className="mt-1 h-1.5" />
      </button>
    );
  };

  const renderMenu = (doc) => {
    const archived = Boolean(doc.archived_at);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`More actions for ${doc.title}`}
            className="h-9 w-9 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:h-8 sm:w-8"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {!archived ? (
            <>
              <DropdownMenuItem
                onClick={() => openForm("edit", doc)}
                className={!canEditDoc ? disabledClass : "cursor-pointer"}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openForm("replace", doc)}
                className={!canEditDoc ? disabledClass : "cursor-pointer"}
              >
                <FileUp className="mr-2 h-4 w-4" />
                Replace file
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => askConfirm("archive", doc)}
                className={!canDeleteDoc ? disabledClass : "cursor-pointer text-amber-700 dark:text-amber-400"}
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem
                onClick={() => handleRestore(doc)}
                className={!canDeleteDoc ? disabledClass : "cursor-pointer"}
              >
                <ArchiveRestore className="mr-2 h-4 w-4" />
                Restore
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => askConfirm("delete", doc)}
                className={!canDeleteDoc ? disabledClass : "cursor-pointer text-red-600 dark:text-red-400"}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete permanently
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  if (loading) {
    return <CompanyDocumentsSkeleton withAction />;
  }

  const uploadButton = (
    <Button
      onClick={() => openForm("create")}
      className={`flex w-full items-center justify-center gap-2 sm:w-auto ${!canCreateDoc ? `${disabledClass} hover:bg-primary` : ""}`}
    >
      <Upload className="h-4 w-4" />
      <span>Upload Document</span>
    </Button>
  );

  const confirmCopy =
    confirmState.action === "delete"
      ? {
          title: "Delete Document Permanently?",
          description:
            "The file and any record of who acknowledged it will be removed. This action cannot be undone.",
          confirmText: "Delete Permanently",
        }
      : {
          title: "Archive Document?",
          description: "Employees will no longer see this document. You can restore it from Archived.",
          confirmText: "Archive",
        };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">Company Documents</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 sm:text-base">
            Organise, share and manage documents for everyone in your company
          </p>
        </div>
        {uploadButton}
      </div>

      {loadError ? (
        <CompanyDocumentsError message={loadError} onRetry={loadDocuments} />
      ) : (
        <>
          <CompanyDocumentCategoryTabs documents={searchedDocs} value={category} onChange={setCategory} />

          <CompanyDocumentsToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filter={<CompanyDocumentCategoryFilter documents={searchedDocs} value={category} onChange={setCategory} />}
          >
            <SegmentedControl
              ariaLabel="Document status"
              value={status}
              onChange={handleStatusChange}
              options={[
                { value: "active", label: `Active (${activeDocs.length})` },
                { value: "archived", label: `Archived (${archivedDocs.length})` },
              ]}
            />
          </CompanyDocumentsToolbar>

          <CompanyDocumentActiveCategory
            value={category}
            count={visibleDocs.length}
            onClear={() => setCategory("all")}
          />

          <CompanyDocumentsGrid
            documents={visibleDocs}
            onOpen={handleOpen}
            renderActions={renderActions}
            renderMenu={renderMenu}
            renderStatus={renderStatus}
            showAudit
            isFiltered={Boolean(searchTerm.trim()) || category !== "all"}
            emptyTitle={status === "archived" ? "No archived documents" : "No documents uploaded yet"}
            emptyDescription={
              status === "archived"
                ? "Documents you archive will appear here, where you can restore or delete them."
                : "Upload your first company document to share it with all employees."
            }
            emptyAction={status === "active" && canCreateDoc ? uploadButton : null}
          />
        </>
      )}

      <CompanyDocumentFormDialog
        open={formState.open}
        mode={formState.mode}
        document={formState.document}
        onOpenChange={(open) => setFormState((s) => ({ ...s, open }))}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={(open) => setConfirmState((s) => ({ ...s, open }))}
        title={confirmCopy.title}
        description={confirmCopy.description}
        message={confirmState.document ? `"${confirmState.document.title}"` : ""}
        confirmText={confirmCopy.confirmText}
        destructive
        onConfirm={handleConfirm}
      />

      <CompanyDocumentPreviewDialog preview={preview} onClose={() => setPreview(null)} />

      <CompanyDocumentAcknowledgementsDialog
        doc={acknowledgementsDoc}
        canRemind={canEditDoc}
        onClose={() => {
          setAcknowledgementsDoc(null);
          loadDocuments();
        }}
      />
    </div>
  );
};

export default CompanyDocuments;
